import { useTranslation } from '@msdk/common'
import {
  EMPTY_CELL_VALUE,
  OptionsetFieldDefinition,
  OptionSetModel,
  OptionsetValue,
  readOptionSetModelByName,
  ViewColumnBehaviour,
  ViewColumnDefinition,
} from '@msdk/definitions'
import { COLORS, computeStatusPropsFromOptionItem, Link, Status, styled } from '@msdk/ui'
import React from 'react'

import { getFieldDefIncludeExternal } from '../../../utils'
import { useDynamicViewContext } from '../context'
import { EllipsisStyle } from './utils'

const Separator = styled('span')`
  color: ${COLORS.text.primary};
  ::before {
    content: ', ';
  }
`

interface Props {
  value: OptionsetValue | null
  colDef: ViewColumnDefinition
  onItemClick?: () => void
}

const CellContainer = styled('div')<{ ellipsis?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  .MuiTypography-root {
    ${(props) => (props.ellipsis ? EllipsisStyle : '')}
  }
`

export function OptionsetCellRenderer({ value, colDef, onItemClick }: Props) {
  const { baseMetadata, viewModel } = useDynamicViewContext()
  const t = useTranslation()
  const fieldDef = getFieldDefIncludeExternal(viewModel, colDef.fieldName, baseMetadata) as OptionsetFieldDefinition
  const optModel = readOptionSetModelByName(fieldDef.family) as OptionSetModel
  const opt = optModel.options.find((op) => op.value === value?.value)

  const optStatusProps = computeStatusPropsFromOptionItem({
    fontColor: opt?.fontColor,
    icon: opt?.icon,
  })

  const handleItemClick = () => {
    onItemClick && onItemClick()
  }

  const translateValue = (optVal: string): string => {
    const o = optModel.options.find((op) => op.value === optVal)
    const translatedOptVal = t(o?.idml ?? '', o?.description ?? '')
    return translatedOptVal
  }

  if (colDef.behaviour === ViewColumnBehaviour.Clickable) {
    return (
      <CellContainer ellipsis={colDef.ellipsis}>
        <Link onClick={handleItemClick}>
          <Status {...optStatusProps}>
            {value ? value.toStringForView() : optStatusProps.icon ? '' : EMPTY_CELL_VALUE}
          </Status>
        </Link>
      </CellContainer>
    )
  }

  if (value?.value?.includes('|')) {
    const values = value.value.split('|')
    if (fieldDef.ignoreAlphabeticalOrder) {
      const rank = new Map(optModel.options.map((v, i) => [v.value, i]))
      values.sort((a, b) => {
        const ra = rank.get(a) ?? Number.POSITIVE_INFINITY
        const rb = rank.get(b) ?? Number.POSITIVE_INFINITY
        return ra - rb
      })
    } else {
      values.sort((a, b) => translateValue(a).localeCompare(translateValue(b)))
    }

    return (
      <CellContainer ellipsis={colDef.ellipsis}>
        {values.map((v, i) => {
          const o = optModel.options.find((op) => op.value === v)

          const multipleOptionStatusProps = computeStatusPropsFromOptionItem({
            fontColor: o?.fontColor,
            icon: o?.icon,
          })

          return (
            <Status key={v} {...multipleOptionStatusProps}>
              {t(o?.idml ?? '', o?.description ?? '')}
              {i !== values.length - 1 && <Separator />}
            </Status>
          )
        })}
      </CellContainer>
    )
  }

  return (
    <CellContainer ellipsis={colDef.ellipsis}>
      <Status {...optStatusProps}>
        {value ? value.toStringForView() : optStatusProps.icon ? '' : EMPTY_CELL_VALUE}
      </Status>
    </CellContainer>
  )
}
